import React, { useState, useEffect } from 'react';
import {
  Users, Search, Star, Briefcase, Clock, DollarSign,
  MessageSquare, CheckCircle, TrendingUp, Award,
  UserPlus, Loader2, Eye, ChevronRight, X, Globe
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../context/LanguageContext';
import { teamsApi } from '../services';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CommunityPage = () => {
  const { t, language } = useTranslation();
  const [activeTab, setActiveTab] = useState('experts');
  const [loading, setLoading] = useState(true);
  const [experts, setExperts] = useState([]);
  const [collaborations, setCollaborations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSpecialization, setSelectedSpecialization] = useState('all');
  const [specializations, setSpecializations] = useState([]);
  const [selectedExpert, setSelectedExpert] = useState(null);
  const navigate = useNavigate();
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'experts') {
        await Promise.all([loadExperts(), loadSpecializations()]);
      } else {
        await loadCollaborations();
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadExperts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/experts/search`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExperts(response.data.experts || []);
    } catch (error) {
      console.error('Failed to load experts:', error);
      toast.error(language === 'en' ? 'Could not load experts' : 'Kunne ikke laste eksperter');
    }
  };

  const loadSpecializations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/experts/specializations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSpecializations(response.data.specializations || []);
    } catch (error) {
      console.error('Failed to load specializations:', error);
    }
  };

  const loadCollaborations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/collaborations/my-collaborations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCollaborations(response.data.collaborations || []);
    } catch (error) {
      console.error('Failed to load collaborations:', error);
      toast.error(language === 'en' ? 'Could not load collaborations' : 'Kunne ikke laste samarbeid');
    }
  };

  const filteredExperts = experts.filter(expert => {
    const matchesSearch = !searchQuery || 
      expert.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      expert.expert_profile?.bio?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      expert.expert_profile?.specializations?.some(s => s.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesSpecialization = selectedSpecialization === 'all' || 
      expert.expert_profile?.specializations?.includes(selectedSpecialization);
    
    return matchesSearch && matchesSpecialization;
  });

  const activeCollabs = collaborations.filter(c => c.status === 'ACTIVE').length;
  const completedCollabs = collaborations.filter(c => c.status === 'COMPLETED').length;
  const pendingCollabs = collaborations.filter(c => c.status === 'PENDING').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto px-8 py-10">
        
        {/* Header */}
        <header className="mb-10">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">Community</h1>
              <p className="text-gray-500 text-sm mt-1">
                {language === 'en' 
                  ? `Collaborate with ${experts.length} experts in the network`
                  : `Samarbeid med ${experts.length} eksperter i nettverket`}
              </p>
            </div>
            {!currentUser.expert_profile?.is_expert && (
              <button
                onClick={() => navigate('/lexy/expert/profile')}
                className="flex items-center gap-2 px-5 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors"
              >
                <Award className="w-4 h-4" />
                {language === 'en' ? 'Become an Expert' : 'Bli ekspert'}
              </button>
            )}
          </div>
        </header>

        {/* Stats Row */}
        <div className="flex items-center gap-10 mb-10 pb-8 border-b border-gray-100">
          <div>
            <div className="text-3xl font-semibold text-gray-900">{experts.length}</div>
            <div className="text-sm text-gray-500">{language === 'en' ? 'Experts' : 'Eksperter'}</div>
          </div>
          <div className="w-px h-10 bg-gray-200" />
          <div>
            <div className="text-3xl font-semibold text-gray-900">{activeCollabs}</div>
            <div className="text-sm text-gray-500">{language === 'en' ? 'Active' : 'Aktive'}</div>
          </div>
          <div className="w-px h-10 bg-gray-200" />
          <div>
            <div className="text-3xl font-semibold text-gray-900">{completedCollabs}</div>
            <div className="text-sm text-gray-500">{language === 'en' ? 'Completed' : 'Fullførte'}</div>
          </div>
          <div className="w-px h-10 bg-gray-200" />
          <div>
            <div className="text-3xl font-semibold text-gray-900">{pendingCollabs}</div>
            <div className="text-sm text-gray-500">{language === 'en' ? 'Pending' : 'Venter'}</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-8">
          <button
            onClick={() => setActiveTab('experts')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'experts'
                ? 'bg-slate-900 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Search className="w-4 h-4 inline mr-2" />
            {language === 'en' ? 'Find Experts' : 'Finn Eksperter'}
          </button>
          <button
            onClick={() => setActiveTab('collaborations')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'collaborations'
                ? 'bg-slate-900 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Briefcase className="w-4 h-4 inline mr-2" />
            {language === 'en' ? 'My Collaborations' : 'Mine Samarbeid'}
          </button>
        </div>

        {activeTab === 'experts' ? (
          <>
            {/* Search */}
            <div className="flex items-center gap-4 mb-8">
              <div className="flex-1 max-w-md relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={language === 'en' ? 'Search experts, specializations...' : 'Søk etter eksperter, spesialiseringer...'}
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border-0 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all"
                />
              </div>
              <select
                value={selectedSpecialization}
                onChange={(e) => setSelectedSpecialization(e.target.value)}
                className="px-4 py-2.5 bg-gray-50 border-0 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
              >
                <option value="all">{language === 'en' ? 'All specializations' : 'Alle spesialiseringer'}</option>
                {specializations.map(spec => (
                  <option key={spec} value={spec}>{spec}</option>
                ))}
              </select>
            </div>

            {/* Experts List */}
            {filteredExperts.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Users className="w-6 h-6 text-gray-400" />
                </div>
                <p className="text-gray-900 font-medium mb-1">
                  {language === 'en' ? 'No experts found' : 'Ingen eksperter funnet'}
                </p>
                <p className="text-gray-500 text-sm">
                  {searchQuery || selectedSpecialization !== 'all' 
                    ? (language === 'en' ? 'Try changing your search criteria' : 'Prøv å endre søkekriteriene') 
                    : (language === 'en' ? 'No experts in the network yet' : 'Ingen eksperter i nettverket ennå')}
                </p>
              </div>
            ) : (
              <div className="space-y-0">
                {filteredExperts.map(expert => (
                  <div
                    key={expert.id}
                    className="group flex items-center gap-4 py-5 cursor-pointer transition-all border-b border-gray-100 last:border-b-0 hover:bg-gray-50 px-3 -mx-3 rounded-lg"
                    onClick={() => setSelectedExpert(expert)}
                  >
                    {/* Avatar */}
                    {(expert.profile_image_url || expert.profile_picture_url) ? (
                      <img 
                        src={expert.profile_image_url || expert.profile_picture_url} 
                        alt={expert.name}
                        className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-slate-900 flex items-center justify-center text-white font-semibold text-lg flex-shrink-0">
                        {expert.name.charAt(0)}
                      </div>
                    )}

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <h3 className="font-medium text-gray-900">{expert.name}</h3>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                          expert.expert_profile?.availability === 'AVAILABLE'
                            ? 'bg-emerald-50 text-emerald-700'
                            : expert.expert_profile?.availability === 'BUSY'
                            ? 'bg-amber-50 text-amber-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {expert.expert_profile?.availability === 'AVAILABLE' 
                            ? (language === 'en' ? 'Available' : 'Tilgjengelig') 
                            : expert.expert_profile?.availability === 'BUSY' 
                            ? (language === 'en' ? 'Busy' : 'Opptatt') 
                            : (language === 'en' ? 'Unavailable' : 'Ikke tilgjengelig')}
                        </span>
                      </div>
                      {expert.expert_profile?.specializations?.length > 0 && (
                        <div className="flex items-center gap-2 mt-1">
                          {expert.expert_profile.specializations.slice(0, 3).map(spec => (
                            <span key={spec} className="text-xs text-gray-500">{spec}</span>
                          ))}
                          {expert.expert_profile.specializations.length > 3 && (
                            <span className="text-xs text-gray-400">+{expert.expert_profile.specializations.length - 3}</span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Stats */}
                    <div className="flex items-center gap-6 text-sm text-gray-500">
                      <div className="text-center">
                        <div className="font-semibold text-gray-900">{expert.expert_profile?.years_experience || 0}</div>
                        <div className="text-xs">{language === 'en' ? 'years' : 'år'}</div>
                      </div>
                      <div className="text-center">
                        <div className="font-semibold text-gray-900">{expert.expert_profile?.hourly_rates?.standard || expert.expert_profile?.hourly_rate || 1500}</div>
                        <div className="text-xs">kr/t</div>
                      </div>
                      <div className="text-center">
                        <div className="font-semibold text-gray-900">{expert.collaboration_count || 0}</div>
                        <div className="text-xs">{language === 'en' ? 'collabs' : 'samarbeid'}</div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toast.info(language === 'en' ? 'Open a case first, then invite experts' : 'Åpne en sak først, deretter kan du invitere eksperter');
                        }}
                        className="px-3 py-1.5 bg-slate-900 text-white text-xs font-medium rounded-lg hover:bg-slate-800 transition-colors"
                      >
                        <UserPlus className="w-3.5 h-3.5 inline mr-1" />
                        {language === 'en' ? 'Invite' : 'Inviter'}
                      </button>
                      <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-colors" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          /* Collaborations Tab */
          <>
            {collaborations.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Briefcase className="w-6 h-6 text-gray-400" />
                </div>
                <p className="text-gray-900 font-medium mb-1">
                  {language === 'en' ? 'No collaborations yet' : 'Ingen samarbeid ennå'}
                </p>
                <p className="text-gray-500 text-sm">
                  {language === 'en' ? 'Invite experts to cases to start collaborating' : 'Inviter eksperter til saker for å starte samarbeid'}
                </p>
              </div>
            ) : (
              <div className="space-y-0">
                {collaborations.map(collab => (
                  <div
                    key={collab.id}
                    className="group flex items-center gap-4 py-5 cursor-pointer transition-all border-b border-gray-100 last:border-b-0 hover:bg-gray-50 px-3 -mx-3 rounded-lg"
                    onClick={() => navigate(`/lexy/case/${collab.case_id}/workspace`)}
                  >
                    {/* Status dot */}
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                      collab.status === 'ACTIVE' ? 'bg-emerald-500' :
                      collab.status === 'COMPLETED' ? 'bg-blue-500' :
                      collab.status === 'PENDING' ? 'bg-amber-500' :
                      'bg-gray-300'
                    }`} />

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 truncate">{collab.case_title}</h3>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {collab.expert_id === currentUser.id 
                          ? `${language === 'en' ? 'Invited by' : 'Invitert av'} ${collab.primary_lawyer_name}`
                          : `${language === 'en' ? 'With' : 'Samarbeid med'} ${collab.expert_name}`}
                      </p>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center gap-6 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {collab.total_hours || 0}t
                      </span>
                      <span className="flex items-center gap-1">
                        <DollarSign className="w-3.5 h-3.5" />
                        {collab.total_cost || 0} kr
                      </span>
                      <span className="flex items-center gap-1">
                        <MessageSquare className="w-3.5 h-3.5" />
                        {collab.message_count || 0}
                      </span>
                    </div>

                    {/* Status Badge */}
                    <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                      collab.status === 'ACTIVE' ? 'bg-emerald-50 text-emerald-700' :
                      collab.status === 'COMPLETED' ? 'bg-blue-50 text-blue-700' :
                      collab.status === 'PENDING' ? 'bg-amber-50 text-amber-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {collab.status === 'ACTIVE' ? (language === 'en' ? 'Active' : 'Aktiv') :
                       collab.status === 'COMPLETED' ? (language === 'en' ? 'Completed' : 'Fullført') :
                       collab.status === 'PENDING' ? (language === 'en' ? 'Pending' : 'Venter') :
                       (language === 'en' ? 'Declined' : 'Avvist')}
                    </span>

                    <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-colors" />
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Expert Profile Modal */}
      {selectedExpert && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setSelectedExpert(null)}>
          <div 
            className="bg-white max-w-lg w-full max-h-[85vh] overflow-y-auto rounded-xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  {(selectedExpert.profile_image_url || selectedExpert.profile_picture_url) ? (
                    <img 
                      src={selectedExpert.profile_image_url || selectedExpert.profile_picture_url} 
                      alt={selectedExpert.name}
                      className="w-14 h-14 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-slate-900 flex items-center justify-center text-white font-semibold text-xl">
                      {selectedExpert.name.charAt(0)}
                    </div>
                  )}
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">{selectedExpert.name}</h2>
                    <p className="text-sm text-gray-500 mt-0.5">{selectedExpert.email}</p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedExpert(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Bio */}
              {selectedExpert.expert_profile?.bio && (
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2">{language === 'en' ? 'About' : 'Om meg'}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{selectedExpert.expert_profile.bio}</p>
                </div>
              )}

              {/* Specializations */}
              {selectedExpert.expert_profile?.specializations?.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2">{language === 'en' ? 'Specializations' : 'Spesialiseringer'}</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedExpert.expert_profile.specializations.map(spec => (
                      <span key={spec} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                        {spec}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">{language === 'en' ? 'Experience' : 'Erfaring'}</p>
                  <p className="text-xl font-semibold text-gray-900">{selectedExpert.expert_profile?.years_experience || 0} {language === 'en' ? 'years' : 'år'}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">{language === 'en' ? 'Collaborations' : 'Samarbeid'}</p>
                  <p className="text-xl font-semibold text-gray-900">{selectedExpert.collaboration_count || 0}</p>
                </div>
              </div>

              {/* Hourly Rates */}
              {selectedExpert.expert_profile?.hourly_rates && (
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2">{language === 'en' ? 'Hourly Rates' : 'Timepriser'}</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500">Standard</p>
                      <p className="font-semibold text-gray-900">{selectedExpert.expert_profile.hourly_rates.standard} kr/t</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500">{language === 'en' ? 'Complex' : 'Kompleks'}</p>
                      <p className="font-semibold text-gray-900">{selectedExpert.expert_profile.hourly_rates.complex} kr/t</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500">{language === 'en' ? 'Urgent' : 'Haster'}</p>
                      <p className="font-semibold text-gray-900">{selectedExpert.expert_profile.hourly_rates.urgent} kr/t</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500">{language === 'en' ? 'Consultation' : 'Konsultasjon'}</p>
                      <p className="font-semibold text-gray-900">{selectedExpert.expert_profile.hourly_rates.consultation} kr/t</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Languages */}
              {selectedExpert.expert_profile?.languages?.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2">{language === 'en' ? 'Languages' : 'Språk'}</h3>
                  <div className="flex gap-2">
                    {selectedExpert.expert_profile.languages.map(lang => (
                      <span key={lang} className="flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                        <Globe className="w-3 h-3" />
                        {lang}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-100">
              <button
                onClick={() => {
                  setSelectedExpert(null);
                  toast.info(language === 'en' ? 'Open a case first, then invite experts' : 'Åpne en sak først, deretter kan du invitere eksperter');
                }}
                className="w-full px-4 py-2.5 bg-slate-900 text-white font-medium rounded-lg hover:bg-slate-800 transition-colors flex items-center justify-center gap-2"
              >
                <UserPlus className="w-4 h-4" />
                {language === 'en' ? 'Invite to Case' : 'Inviter til sak'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunityPage;
